import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Spinner } from '../../Spinner';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { usePageLoadBi } from '../../../lib/use-page-load-bi';
import { redirectToLogin } from '../../../lib/auth-redirect';
import { profileLinksApi } from '../../../lib/profile-links-api';
import {
  classifyOpenFailure,
  clearPendingProfileThread,
  openResultHref,
  parseProfileThreadQuery,
  rememberProfileThread,
  savePendingProfileThread,
  type OpenFailure,
} from '../../../lib/profile-thread-target';
import { Button, Typography } from '../ui';
import { PROFILE_THREAD_PAGE } from './constants';
import styles from './ProfileLinks.module.css';

/**
 * `/profile-thread?handle=&context=` — a signed-in visitor opening someone's profile link on the web
 * (docs/symphony-profile-links-design.md §6.1). One POST, `open-profile-thread`, made from the page and never
 * on the GET that loaded it (§5.5); the server resolves the link and answers a conversation the caller can open —
 * or `own_link` when the owner scanned their own code, which opens the editor instead of a thread with themselves.
 *
 * The referral: `OpenProfileThread` itself records none. After an open that reached a thread, the native-data
 * action this POST is proxied to (ai-scheduling-assistant-server `handlers/profileLinkReferral.ts`) credits the
 * link's OWNER with the caller — resolved from the handle, never from this request — directly when the caller
 * has an email and a phone, otherwise parked on the caller's own recipient for the phone verification to spend.
 * First touch, never self. The same action serves both phones, so all three surfaces credit alike.
 *
 * The pending target (lib/profile-thread-target.ts) is cleared on every terminal outcome, so a resume can
 * never loop; it is saved again on a 401, so the open survives the sign-in it triggers.
 */
type OpenState = { kind: 'opening' } | { kind: 'failed'; failure: OpenFailure };

export function ProfileThreadOpener() {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const [state, setState] = useState<OpenState>({ kind: 'opening' });
  const [attempt, setAttempt] = useState(0);
  const started = useRef(-1);

  usePageLoadBi(PROFILE_THREAD_PAGE, {
    enabled: state.kind === 'failed',
    isSuccessful: false,
    ...(state.kind === 'failed' ? { additionalFields: { reason: state.failure } } : {}),
  });

  useEffect(() => {
    if (!router.isReady || started.current === attempt) return;
    started.current = attempt;
    const route = parseProfileThreadQuery(router.query as Record<string, string | string[] | undefined>);
    if (!route) {
      clearPendingProfileThread();
      setState({ kind: 'failed', failure: 'unavailable' });
      return;
    }
    setState({ kind: 'opening' });
    profileLinksApi
      .openProfileThread(route.handle, route.context)
      .then((result) => {
        clearPendingProfileThread();
        if (result.kind === 'thread') {
          rememberProfileThread(result.conversationId, { ownerName: result.ownerName, contextTitle: result.contextTitle });
        }
        // A FULL navigation, as the pack accept does: the chat adopts `?conversation=` only on its own first
        // mount, and `replace` so Back never re-opens the thread.
        window.location.replace(`${basePath}${openResultHref(result)}`);
      })
      .catch((err: unknown) => {
        const failure = classifyOpenFailure(err);
        if (failure === 'signin') {
          savePendingProfileThread(route, Date.now());
          redirectToLogin();
          return;
        }
        if (failure !== 'failed') clearPendingProfileThread();
        setState({ kind: 'failed', failure });
      });
  }, [router.isReady, router.query, basePath, attempt]);

  if (state.kind === 'opening') {
    return (
      <div className={styles.opener} role="status">
        <Spinner size={20} />
        <Typography variant="p1" color="secondary" component="p">
          {t('profileLinks.thread.opening')}
        </Typography>
      </div>
    );
  }

  const titleKey =
    state.failure === 'not_ready' ? 'profileLinks.unavailable.title' : state.failure === 'unavailable' ? 'profileLink.unavailable.title' : 'profileLinks.thread.failed';
  return (
    <div className={styles.opener}>
      <Typography variant="h3" component="h1">
        {t(titleKey)}
      </Typography>
      <div className={styles.actions}>
        {state.failure === 'failed' ? (
          <Button
            onClick={() => {
              logPageClickBi({ ...PROFILE_THREAD_PAGE, button_name: 'retry' });
              setAttempt((n) => n + 1);
            }}
          >
            {t('common.retry')}
          </Button>
        ) : null}
        <Button variant="outlined" href="/" onClick={() => logPageClickBi({ ...PROFILE_THREAD_PAGE, button_name: 'go_home' })}>
          {t('profileLinks.thread.goHome')}
        </Button>
      </div>
    </div>
  );
}
